import React, { useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { css } from '@emotion/css'
import Swal from 'sweetalert2'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../base/customHooks'
import { uuidv4 } from '../../../../base/utils/dataHelper'
import IPButton from '../../../../components/IPButton'
import IPModal from '../../../../components/IPModal'
import IPInput from '../../../../components/IPInput'
import IPSelectBox from '../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../components/IPSelectBox/IPSelectBox'
import InputCampaign from '../../../../components/InputCampaign'
import Table from '../../../../components/Table'
import {
  formatDateTime,
  installmentOptions,
  IPkRateGroup,
  IPkRateHistory,
  IPkRateRow,
  PK_COMPANY_DATAGATE,
  PK_NORMAL_POS_CASH_ONLY_BANK_IDS,
  PK_POS_AKODE,
  PK_POS_NORMAL,
  pkPosTypeLabel,
  pkPosTypesLabel,
  toDateTimeInput,
  toApiDateTime,
} from '../models/models'
import { ListPkRatesApi } from '../services/listPkRatesApi'
import { AddPkRatesApi } from '../services/addPkRatesApi'
import { UpdatePkRateApi } from '../services/updatePkRateApi'
import { DeletePkRateApi } from '../services/deletePkRateApi'
import { ListPkRateHistoryApi } from '../services/listPkRateHistoryApi'
import { GetPkBankListApi } from '../services/getPkBankListApi'

interface IAddRow {
  key: string
  installment?: ISelectOption | null
  rate: string
}

interface IPkRatesState {
  groups: IPkRateGroup[]
  banks: ISelectOption[]
  // Liste filtresi (POS türü)
  posTypeFilter: ISelectOption
  // Oran ekleme modalı
  showAdd: boolean
  addPosTypes: number[]
  selectedBanks: ISelectOption[]
  addStart: string
  addEnd: string
  addRows: IAddRow[]
  // Satır görüntüleme / düzenleme modalı
  activeGuid: string
  showRows: boolean
  editingRowId?: number
  editRate: string
  editStart: string
  editEnd: string
  // Tarihçe modalı
  showHistory: boolean
  history: IPkRateHistory[]
}

const companyOptions: ISelectOption[] = [{ value: PK_COMPANY_DATAGATE, label: 'Datagate' }]
const posTypeChoices = [PK_POS_AKODE, PK_POS_NORMAL]
const allPosTypesOption: ISelectOption = { value: 0, label: 'Tümü' }
const posTypeFilterOptions: ISelectOption[] = [allPosTypesOption, ...posTypeChoices.map(t => ({ value: t, label: pkPosTypeLabel(t) }))]
// Grubun POS türleri; yanıtta alan yoksa satırlardan türetilir.
const groupPosTypes = (g: IPkRateGroup): number[] =>
  g.PosTypes && g.PosTypes.length > 0 ? g.PosTypes : Array.from(new Set((g.Rates || []).map(r => r.PosType).filter(t => t != null)))
const rateInstallmentOptions = installmentOptions(1, 12)
const iconButton = css`
  cursor: pointer;
  margin-right: 8px;
`
const emptyRow = (): IAddRow => ({ key: uuidv4(), installment: null, rate: '' })

function PkRates(props: BaseComponentWithContextProps) {
  const [state, setState] = useCustomReducer<IPkRatesState>({
    groups: [],
    banks: [],
    posTypeFilter: allPosTypesOption,
    showAdd: false,
    addPosTypes: [],
    selectedBanks: [],
    addStart: toDateTimeInput(new Date()),
    addEnd: '',
    addRows: [emptyRow()],
    activeGuid: '',
    showRows: false,
    editRate: '',
    editStart: '',
    editEnd: '',
    showHistory: false,
    history: [],
  })

  const showError = (err: any) =>
    Swal.fire({ icon: 'error', title: err?.description, showConfirmButton: true, allowOutsideClick: false })
  const showWarning = (title: string) =>
    Swal.fire({ icon: 'error', title, showConfirmButton: true, allowOutsideClick: false })

  useEffect(() => {
    loadAll()
  }, [])

  const fetchGroups = async () => await new ListPkRatesApi({}).listRates()

  const loadAll = async () => {
    props.showLoading()
    try {
      const groups = await fetchGroups()
      setState({ groups: groups || [] })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const activeGroup = useMemo(() => state.groups.find(g => g.Guid === state.activeGuid), [state.groups, state.activeGuid])

  // ---- Ekleme ----
  const openAdd = () =>
    setState({
      showAdd: true,
      addPosTypes: [],
      banks: [],
      selectedBanks: [],
      addStart: toDateTimeInput(new Date()),
      addEnd: '',
      addRows: [emptyRow()],
    })

  // Banka seçenekleri seçilen POS türlerine göre gelir; iki tür seçiliyse yalnız iki türde de kaydı olan bankalar listelenir.
  // Seçim hemen state'e yazılır; arka arkaya tıklamada yalnız son isteğin cevabı uygulanır.
  const bankRequestSeq = useRef(0)
  const stateRef = useRef(state)
  stateRef.current = state
  const togglePosType = async (posType: number, checked: boolean) => {
    const posTypes = posTypeChoices.filter(t => (t === posType ? checked : state.addPosTypes.includes(t)))
    const seq = ++bankRequestSeq.current
    if (posTypes.length === 0) {
      setState({ addPosTypes: posTypes, banks: [], selectedBanks: [] })
      return
    }
    setState({ addPosTypes: posTypes })
    props.showLoading()
    try {
      const lists = await Promise.all(posTypes.map(t => new GetPkBankListApi({}).getBanks(t)))
      if (seq !== bankRequestSeq.current) return
      const [first, ...rest] = lists.map(list => list || [])
      const banks: ISelectOption[] = first
        .filter(b => rest.every(list => list.some(x => x.BankId === b.BankId)))
        .map(b => ({ label: b.BankName, value: b.BankId }))
      const available = new Set(banks.map(b => String(b.value)))
      setState({
        banks,
        selectedBanks: stateRef.current.selectedBanks.filter(b => available.has(String(b.value))),
      })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  // Normal POS peşin kısıtına takılan seçili bankalar
  const cashOnlyBanks = useMemo(
    () =>
      state.addPosTypes.includes(PK_POS_NORMAL)
        ? state.selectedBanks.filter(b => PK_NORMAL_POS_CASH_ONLY_BANK_IDS.includes(Number(b.value)))
        : [],
    [state.addPosTypes, state.selectedBanks]
  )

  const updateAddRow = (key: string, patch: Partial<IAddRow>) =>
    setState({ addRows: state.addRows.map(r => (r.key === key ? { ...r, ...patch } : r)) })

  const changeAddInstallment = (key: string, option: ISelectOption | null) => {
    if (option && state.addRows.some(r => r.key !== key && String(r.installment?.value) === String(option.value))) {
      showWarning('Taksit daha önce seçilmiştir.')
      return
    }
    updateAddRow(key, { installment: option })
  }

  const parseRate = (value: string): number | null => {
    const n = Number(String(value).replace(',', '.'))
    return value.trim() === '' || isNaN(n) || n < 0 || n > 100 ? null : n
  }

  const saveAdd = async () => {
    if (state.addPosTypes.length === 0) return showWarning('Lütfen en az bir POS türü seçiniz.')
    if (state.selectedBanks.length === 0) return showWarning('Lütfen en az bir banka seçiniz.')
    if (!state.addStart || !state.addEnd) return showWarning('Başlangıç ve bitiş tarihi zorunludur.')
    if (state.addEnd < state.addStart) return showWarning('Bitiş tarihi başlangıç tarihinden önce olamaz.')
    if (state.addRows.length === 0 || state.addRows.some(r => !r.installment || r.rate.trim() === '')) {
      return showWarning('Lütfen taksit ve oran alanlarını boş bırakmayınız.')
    }
    if (state.addRows.some(r => parseRate(r.rate) === null)) return showWarning('Oran 0-100 arasında bir sayı olmalıdır.')
    if (cashOnlyBanks.length > 0 && state.addRows.some(r => Number(r.installment?.value) !== 1)) {
      return showWarning(
        `Normal POS'ta ${cashOnlyBanks.map(b => b.label).join(', ')} için yalnız peşin (1 taksit) oran tanımlanabilir. Bu bankaları ayrı kayıtla ekleyiniz ya da yalnız 1 taksit giriniz.`
      )
    }

    props.showLoading()
    try {
      await new AddPkRatesApi({}).addRates({
        BankIds: state.selectedBanks.map(b => Number(b.value)),
        Company: PK_COMPANY_DATAGATE,
        PosTypes: state.addPosTypes,
        Rates: state.addRows.map(r => ({ Installment: Number(r.installment?.value), InterestRate: parseRate(r.rate) as number })),
        StartDate: toApiDateTime(state.addStart),
        EndDate: toApiDateTime(state.addEnd),
      })
      setState({ showAdd: false, groups: (await fetchGroups()) || [] })
      Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  // ---- Satır düzenleme ----
  const startEditRow = (row: IPkRateRow) =>
    setState({
      editingRowId: row.Id,
      editRate: String(row.InterestRate),
      editStart: toDateTimeInput(row.StartDate),
      editEnd: toDateTimeInput(row.EndDate),
    })

  const cancelEditRow = () => setState({ editingRowId: undefined, editRate: '', editStart: '', editEnd: '' })

  const saveEditRow = async () => {
    const rate = parseRate(state.editRate)
    if (rate === null) return showWarning('Oran 0-100 arasında bir sayı olmalıdır.')
    if (!state.editStart || !state.editEnd) return showWarning('Başlangıç ve bitiş tarihi zorunludur.')
    if (state.editEnd < state.editStart) return showWarning('Bitiş tarihi başlangıç tarihinden önce olamaz.')

    props.showLoading()
    try {
      await new UpdatePkRateApi({}).updateRate({
        Id: Number(state.editingRowId),
        InterestRate: rate,
        StartDate: toApiDateTime(state.editStart),
        EndDate: toApiDateTime(state.editEnd),
      })
      setState({ groups: (await fetchGroups()) || [], editingRowId: undefined, editRate: '', editStart: '', editEnd: '' })
      Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  // ---- Silme / tarihçe ----
  const deleteGroup = (guid: string) => {
    Swal.fire({
      icon: 'question',
      title: 'Silmek istediğinize emin misiniz?',
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(async result => {
      if (!result.isConfirmed) return
      props.showLoading()
      try {
        await new DeletePkRateApi({}).deleteRate(guid)
        setState({ groups: (await fetchGroups()) || [] })
        Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })
      } catch (err: any) {
        showError(err)
      } finally {
        props.hideLoading()
      }
    })
  }

  const openHistory = async (guid: string) => {
    props.showLoading()
    try {
      const history = await new ListPkRateHistoryApi({}).listRateHistory(guid)
      setState({ showHistory: true, history: history || [] })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const showBanks = (names: string[]) =>
    Swal.fire({
      title: `Bankalar (${names.length})`,
      html: `<ul style="text-align:left;max-height:400px;overflow-y:auto;margin:0;padding-left:20px">${names
        .map(n => `<li>${n.replace(/</g, '&lt;')}</li>`)
        .join('')}</ul>`,
      confirmButtonText: 'Kapat',
    })

  const columns = useMemo(
    () => [
      { Header: '#', accessor: 'col1' },
      { Header: 'Banka', accessor: 'col2' },
      { Header: 'Şirket', accessor: 'col3' },
      { Header: 'POS Türü', accessor: 'colPos' },
      { Header: 'Taksitler', accessor: 'col4' },
      { Header: 'Başlangıç Tarihi', accessor: 'col5' },
      { Header: 'Bitiş Tarihi', accessor: 'col6' },
      { Header: 'Ekleyen', accessor: 'col7' },
      { Header: 'Değişiklikler', accessor: 'col8' },
      { Header: 'İşlemler', accessor: 'col9' },
    ],
    []
  )

  const filteredGroups = useMemo(() => {
    const filter = Number(state.posTypeFilter?.value || 0)
    return filter === 0 ? state.groups : state.groups.filter(g => groupPosTypes(g).includes(filter))
  }, [state.groups, state.posTypeFilter])

  const tableData = useMemo(
    () =>
      filteredGroups.map((g, index) => ({
        col1: index + 1,
        // Banka listesi uzun olabilir (toplu ekleme): ilk iki banka ve kalan sayısı gösterilir, tamamı tıklanınca açılır.
        col2:
          g.Banks.length <= 2 ? (
            g.Banks.map(b => b.BankName).join(', ')
          ) : (
            <span>
              {g.Banks.slice(0, 2)
                .map(b => b.BankName)
                .join(', ')}{' '}
              <a role='button' className='text-primary' style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => showBanks(g.Banks.map(b => b.BankName))}>
                +{g.Banks.length - 2} banka
              </a>
            </span>
          ),
        col3: companyOptions.find(c => Number(c.value) === g.Company)?.label ?? g.Company,
        colPos: <span style={{ whiteSpace: 'nowrap' }}>{pkPosTypesLabel(groupPosTypes(g))}</span>,
        col4: Array.from(new Set(g.Rates.map(r => r.Installment)))
          .sort((a, b) => a - b)
          .join(', '),
        col5: <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(g.StartDate)}</span>,
        col6: <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(g.EndDate)}</span>,
        col7: g.UserName,
        col8: (
          <IPButton
            text='Tarihçe'
            type='button'
            className='btn btn-danger'
            style={{ padding: '0px', width: '120px', height: '30px', color: 'white', borderColor: 'red' }}
            onClick={() => openHistory(g.Guid)}
          />
        ),
        col9: (
          <>
            <a role='button' className={iconButton} title='Detay / Düzenle' onClick={() => setState({ showRows: true, activeGuid: g.Guid })}>
              <i className={`${css`font-size: 1.2rem; color: green;`} icon-edit`}></i>
            </a>
            <a role='button' className={iconButton} title='Sil' onClick={() => deleteGroup(g.Guid)}>
              <i className={`${css`font-size: 1.2rem; color: red;`} icon-trash-2`}></i>
            </a>
          </>
        ),
      })),
    [filteredGroups]
  )

  const sortedRows = useMemo(
    () =>
      [...(activeGroup?.Rates || [])].sort(
        (a, b) =>
          a.BankName.localeCompare(b.BankName, 'tr') ||
          pkPosTypeLabel(a.PosType).localeCompare(pkPosTypeLabel(b.PosType), 'tr') ||
          a.Installment - b.Installment
      ),
    [activeGroup]
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center flex-wrap'>
            <div className='col-12 col-md-6 mb-2'>
              <span className='h4'>Peşine Kontrat Oran Tanımları</span>
            </div>
            <div className='col-12 col-md-6 mb-2 d-flex justify-content-end'>
              <Link to='/admin/PesineKontrat/Settings'>
                <IPButton text='PK Ayarları' type='button' className='btn btn-primary' style={{ width: 'auto', height: '45px', padding: '0 20px', marginRight: '10px' }} />
              </Link>
              <IPButton text='Ekle' type='button' className='btn btn-primary' style={{ width: 'auto', height: '45px', padding: '0 20px' }} onClick={openAdd} />
            </div>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-12 col-md-3 mb-2'>
                <label className='labeltext'>POS Türü</label>
                <IPSelectBox
                  isClearable={false}
                  menuPortalTarget={document.body}
                  options={posTypeFilterOptions}
                  value={state.posTypeFilter}
                  onChangeSingle={option => setState({ posTypeFilter: option || allPosTypesOption })}
                />
              </div>
              <div className='col-lg-12'>
                <div id='datatable_wrapper' className='dataTables_wrapper dt-bootstrap4' style={{ padding: '10px' }}>
                  <div className='row'>
                    <div className='col-sm-12 pr-4' style={{ overflowX: 'auto' }}>
                      <Table columns={columns} data={tableData} currentPage={0} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <IPModal
        show={state.showAdd}
        onClose={() => setState({ showAdd: false })}
        onConfirm={saveAdd}
        confirmText='Kaydet'
        width={1000}
        title='Peşine Kontrat Oran Ekleme'
      >
        <div>
          <div className='form-row mb-2'>
            <div className='col-lg-12'>
              <label className='labeltext' style={{ marginRight: '20px' }}>
                POS Türü
              </label>
              {posTypeChoices.map(t => (
                <label key={t} className='labeltext' style={{ marginRight: '20px', cursor: 'pointer' }}>
                  <input type='checkbox' checked={state.addPosTypes.includes(t)} onChange={e => togglePosType(t, e.target.checked)} /> {pkPosTypeLabel(t)}
                </label>
              ))}
              <small className='form-text text-muted'>
                En az biri seçilmelidir. İkisi birlikte seçilirse oranlar iki türe de yazılır ve yalnız iki türde de tanımlı bankalar listelenir.
              </small>
            </div>
          </div>
          <div className='form-row'>
            <div className='col-lg-4'>
              <div className='d-flex justify-content-between align-items-center'>
                <label className='labeltext'>Banka</label>
                <div>
                  <a role='button' className='text-primary' style={{ cursor: 'pointer', fontSize: '0.85rem', marginRight: '10px' }} onClick={() => setState({ selectedBanks: state.banks })}>
                    Tümünü Ekle
                  </a>
                  <a role='button' className='text-danger' style={{ cursor: 'pointer', fontSize: '0.85rem' }} onClick={() => setState({ selectedBanks: [] })}>
                    Temizle
                  </a>
                </div>
              </div>
              <IPSelectBox
                isMulti
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                isDisabled={state.addPosTypes.length === 0}
                placeholder={state.addPosTypes.length === 0 ? 'Önce POS türü seçiniz' : 'Banka seçiniz'}
                options={state.banks}
                value={state.selectedBanks}
                onChangeMulti={options => setState({ selectedBanks: options || [] })}
              />
            </div>
            <div className='col-lg-2'>
              <label className='labeltext'>Şirket</label>
              <IPSelectBox isClearable={false} isDisabled={companyOptions.length === 1} menuPortalTarget={document.body} options={companyOptions} value={companyOptions[0]} />
            </div>
            <div className='col-lg-3'>
              <label className='form-label'>Başlangıç Tarihi</label>
              <InputCampaign type='datetime-local' id='PkAddStart' label='' isActive value={state.addStart} onChange={e => setState({ addStart: e.target.value })} />
            </div>
            <div className='col-lg-3'>
              <label className='form-label'>Bitiş Tarihi</label>
              <InputCampaign type='datetime-local' id='PkAddEnd' label='' isActive value={state.addEnd} onChange={e => setState({ addEnd: e.target.value })} />
            </div>
          </div>
          {cashOnlyBanks.length > 0 && (
            <div className='alert alert-warning mt-2 mb-0' role='alert'>
              Normal POS'ta {cashOnlyBanks.map(b => b.label).join(', ')} yalnız peşin (1 taksit) tanımlanabilir; bu bankalarda Normal POS Akbank
              POS'una düşer. Bu kayıtta 2 ve üzeri taksit girilemez.
            </div>
          )}
          {state.addRows.map(row => (
            <div className='row m-t-5' key={row.key}>
              <div className='col-4'>
                <label className='labeltext'>Taksit Adedi</label>
                <IPSelectBox
                  isClearable
                  menuPortalTarget={document.body}
                  options={rateInstallmentOptions}
                  value={row.installment}
                  onChangeSingle={option => changeAddInstallment(row.key, option)}
                />
              </div>
              <div className='col-4'>
                <label className='labeltext'>Vade(max) %</label>
                <IPInput type='text' value={row.rate} onChange={e => updateAddRow(row.key, { rate: e.target.value.replace(',', '.') })} />
              </div>
              <div className='col-2'>
                <label className='labeltext'></label>
                <IPButton
                  type='button'
                  className='btn btn-danger m-t-5'
                  text='Sil'
                  disabled={state.addRows.length < 2}
                  onClick={() => setState({ addRows: state.addRows.filter(r => r.key !== row.key) })}
                />
              </div>
              <div className='col-2'>
                <label className='labeltext'></label>
                <IPButton type='button' className='btn btn-success m-t-5' text='Ekle' onClick={() => setState({ addRows: [...state.addRows, emptyRow()] })} />
              </div>
            </div>
          ))}
        </div>
      </IPModal>

      <IPModal
        show={state.showRows}
        onClose={() => {
          cancelEditRow()
          setState({ showRows: false })
        }}
        hideConfirm={true}
        width={1100}
        title='Peşine Kontrat Oran Detayı'
      >
        <table className='table table-bordered table-hover dataTable' role='grid'>
          <thead className='tableodd'>
            <tr role='row'>
              <th>Banka</th>
              <th>POS Türü</th>
              <th>Taksit</th>
              <th>Vade(max) %</th>
              <th>Başlangıç Tarihi</th>
              <th>Bitiş Tarihi</th>
              <th>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => (
              <tr role='row' className='odd' key={row.Id}>
                <td>{row.BankName}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{pkPosTypeLabel(row.PosType)}</td>
                <td>{row.Installment}</td>
                {state.editingRowId === row.Id ? (
                  <>
                    <td style={{ minWidth: '120px' }}>
                      <IPInput type='text' value={state.editRate} onChange={e => setState({ editRate: e.target.value.replace(',', '.') })} />
                    </td>
                    <td style={{ minWidth: '210px' }}>
                      <InputCampaign type='datetime-local' id='PkEditStart' label='' isActive value={state.editStart} onChange={e => setState({ editStart: e.target.value })} />
                    </td>
                    <td style={{ minWidth: '210px' }}>
                      <InputCampaign type='datetime-local' id='PkEditEnd' label='' isActive value={state.editEnd} onChange={e => setState({ editEnd: e.target.value })} />
                    </td>
                    <td>
                      <a role='button' className={iconButton} title='Kaydet' onClick={saveEditRow}>
                        <i className={`${css`font-size: 1.2rem; color: green;`} icon-check`}></i>
                      </a>
                      <a role='button' className={iconButton} title='İptal' onClick={cancelEditRow}>
                        <i className={`${css`font-size: 1.2rem; color: gray;`} icon-x`}></i>
                      </a>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{row.InterestRate}</td>
                    <td>{formatDateTime(row.StartDate)}</td>
                    <td>{formatDateTime(row.EndDate)}</td>
                    <td>
                      <a role='button' className={iconButton} title='Düzenle' onClick={() => startEditRow(row)}>
                        <i className={`${css`font-size: 1.2rem; color: green;`} icon-edit`}></i>
                      </a>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </IPModal>

      <IPModal show={state.showHistory} onClose={() => setState({ showHistory: false })} hideConfirm={true} width={1300} title='Peşine Kontrat Oran Tarihçe'>
        <table className='table table-bordered table-hover dataTable' role='grid'>
          <thead className='tableodd'>
            <tr role='row'>
              <th>#</th>
              <th>Değişiklik Tarihi</th>
              <th>Banka</th>
              <th>POS Türü</th>
              <th>Taksit</th>
              <th>Vade(max) %</th>
              <th>Başlangıç Tarihi</th>
              <th>Bitiş Tarihi</th>
              <th>Kullanıcı</th>
              <th>İşlem Detayı</th>
            </tr>
          </thead>
          <tbody>
            {state.history.map((h, index) => (
              <tr role='row' className='odd' key={`${h.Guid}-${h.RateId}-${h.CreatedDate}-${index}`}>
                <td>{index + 1}</td>
                <td>{new Date(h.CreatedDate).toLocaleString('tr-TR')}</td>
                <td>{h.BankName}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{pkPosTypeLabel(h.PosType)}</td>
                <td>{h.Installment}</td>
                <td>{h.InterestRate}</td>
                <td>{formatDateTime(h.StartDate)}</td>
                <td>{formatDateTime(h.EndDate)}</td>
                <td>{h.UserName}</td>
                <td>{h.ProcessInformation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </IPModal>
    </div>
  )
}

export default componentWithContext(PkRates)
